/**
 * Presentation helpers for Folder Runs (spec 17-09).
 *
 * A **Run** (Folder Run) is one folder + one configuration + one engine version executed at a
 * point in time; an **Analysis** is the execution of a single video inside it. Everything in this
 * module is pure so the three screens (/runs, /runs/:id and the compare dialog) and the unit
 * tests share exactly the same wording — the UI vocabulary is frozen by the spec:
 *
 *   Freshness    → Current / Outdated / Config derived / Unknown provenance
 *   Comparability→ Comparable / Attributable / Not attributable
 *
 * Every helper tolerates a missing field: the backend is implemented in parallel and a run that
 * predates engine stamping legitimately has `engine_versions: null`. Missing data reads as an
 * em dash, never as a crash.
 */
import { metricStatusColor } from '@/utils/metricsSeries';

/** Em dash used for every unknown value, so the screens never print "null" or "NaN". */
export const EMPTY = '—';

/**
 * The three Coherence Evaluation metrics, in the order the PO reads them. `field` is the key in
 * `FolderRunMetrics` AND in the `delta` block, so one list drives both. Targets are the PO's
 * 70% / 2% goal; `direction: 'max'` means lower is better.
 */
export const FOLDER_RUN_METRICS = Object.freeze([
  {
    key: 'incoherents_detected_pct',
    label: 'Lies detected',
    short: 'Lies',
    target: 70,
    direction: 'min',
  },
  {
    key: 'false_positives_pct',
    label: 'False alarms',
    short: 'False alarms',
    target: 2,
    direction: 'max',
  },
  {
    key: 'correct_videos_pct',
    label: 'Correct videos',
    short: 'Correct',
    target: 70,
    direction: 'min',
  },
]);

const METRIC_BY_KEY = new Map(FOLDER_RUN_METRICS.map((metric) => [metric.key, metric]));

export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Trailing zeros hurt scanning: 58 stays "58%", 57.5 stays "57.5%". */
export function formatPct(value, { digits = 1 } = {}) {
  const n = toNumber(value);
  if (n === null) return EMPTY;
  const rounded = Math.round(n * 10 ** digits) / 10 ** digits;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(digits)}%`;
}

export function formatCount(value) {
  const n = toNumber(value);
  return n === null ? EMPTY : String(n);
}

/** Target status of a metric value: 'ok' when it meets the PO target, 'ko' when it does not. */
export function metricTargetStatus(metricKey, value) {
  const metric = METRIC_BY_KEY.get(metricKey);
  const n = toNumber(value);
  if (!metric || n === null) return 'none';
  return (metric.direction === 'max' ? n <= metric.target : n >= metric.target) ? 'ok' : 'ko';
}

export function metricTargetColor(metricKey, value) {
  return metricStatusColor(metricTargetStatus(metricKey, value));
}

/**
 * Signed delta in percentage points: '+3.2 pts', '−1 pts', '0 pts', '—'.
 * The minus sign is U+2212, as in the rest of the app (metricsSeries.formatDelta).
 */
export function formatDeltaPoints(value, { digits = 1, unit = 'pts' } = {}) {
  const n = toNumber(value);
  if (n === null) return EMPTY;
  const rounded = Math.round(n * 10 ** digits) / 10 ** digits;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  const magnitude = Math.abs(rounded);
  const body = Number.isInteger(magnitude) ? magnitude : magnitude.toFixed(digits);
  return `${sign}${body}${unit ? ` ${unit}` : ''}`;
}

/** Arrow that matches the sign, not the meaning (the colour carries the meaning). */
export function deltaIcon(value) {
  const n = toNumber(value);
  if (n === null || n === 0) return 'mdi-minus';
  return n > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
}

/** 'good' | 'bad' | 'neutral' | 'none' — a rise in false alarms is bad, a rise in the rest good. */
export function deltaTone(metricKey, value) {
  const metric = METRIC_BY_KEY.get(metricKey);
  const n = toNumber(value);
  if (!metric || n === null) return 'none';
  if (n === 0) return 'neutral';
  const better = metric.direction === 'max' ? n < 0 : n > 0;
  return better ? 'good' : 'bad';
}

/**
 * Colour class for a delta. A `not_attributable` verdict greys the number out: the movement is
 * real but it cannot be credited to any single change, so it must not read as a result.
 */
export function deltaColorClass(metricKey, value, verdict = null) {
  if (verdict === 'not_attributable') return 'grey--text';
  const tone = deltaTone(metricKey, value);
  if (tone === 'good') return 'success--text';
  if (tone === 'bad') return 'error--text';
  return 'grey--text text--darken-1';
}

/* -------------------------------------------------------------------------- */
/* Freshness                                                                   */
/* -------------------------------------------------------------------------- */

const FRESHNESS_PRESENTATION = Object.freeze({
  current: {
    state: 'current',
    label: 'Current',
    color: 'success',
    icon: 'mdi-check-circle-outline',
    fallbackDetail: 'This run used the engine versions and configuration deployed right now.',
  },
  outdated: {
    state: 'outdated',
    label: 'Outdated',
    color: 'warning',
    icon: 'mdi-alert-outline',
    fallbackDetail: 'This run used an engine version that is no longer deployed.',
  },
  config_derived: {
    state: 'config_derived',
    label: 'Config derived',
    color: 'info',
    icon: 'mdi-tune-variant',
    fallbackDetail: 'Same engine versions, but a newer version of the configuration exists.',
  },
  unknown: {
    state: 'unknown',
    label: 'Unknown provenance',
    color: 'grey',
    icon: 'mdi-help-circle-outline',
    fallbackDetail: 'The engine versions of this run were not recorded.',
  },
});

/**
 * Chip descriptor for the freshness block of a Folder Run summary.
 * Accepts the whole `freshness` object, a bare state string, or nothing at all.
 *
 * @param {{ state?: string, detail?: string, outdated_engines?: Array, newer_config_version?: number }|string|null} freshness
 * @returns {{ state: string, label: string, color: string, icon: string, detail: string }}
 */
export function freshnessChip(freshness) {
  const raw = typeof freshness === 'string' ? { state: freshness } : freshness || {};
  const key = typeof raw.state === 'string' ? raw.state.toLowerCase() : '';
  const preset = FRESHNESS_PRESENTATION[key] || FRESHNESS_PRESENTATION.unknown;
  const detail =
    typeof raw.detail === 'string' && raw.detail.trim() ? raw.detail.trim() : preset.fallbackDetail;
  return {
    state: preset.state,
    label: preset.label,
    color: preset.color,
    icon: preset.icon,
    detail,
  };
}

/** 'gestures 2.0.3 → 2.1.0 · coherence 1.4.2 → 1.5.0', for the Outdated tooltip. */
export function describeOutdatedEngines(outdatedEngines) {
  const list = Array.isArray(outdatedEngines) ? outdatedEngines : [];
  return list
    .filter((entry) => entry && entry.engine)
    .map((entry) => `${entry.engine} ${entry.run ?? EMPTY} → ${entry.current ?? EMPTY}`)
    .join(' · ');
}

/** Banner shown on /runs when some runs are stale. Wording frozen by the spec. */
export function outdatedRunsNotice(count) {
  const n = toNumber(count) || 0;
  if (n <= 0) return '';
  return `${n} ${n === 1 ? 'run uses' : 'runs use'} an engine version that is no longer deployed`;
}

/* -------------------------------------------------------------------------- */
/* Comparability                                                               */
/* -------------------------------------------------------------------------- */

/** The variables the backend compares, in the order it reports them. */
export const COMPARABILITY_VARIABLES = Object.freeze({
  folder: 'the folder',
  members: 'the set of videos',
  config: 'the configuration',
  engines: 'the engine selection',
  // Singular on purpose: it has to read well inside "Only <variable> differs."
  engine_versions: 'the engine version',
  provenance: 'the run provenance',
});

/** Human name of a differing variable; unknown keys degrade to their own name. */
export function variableLabel(name) {
  if (!name) return 'a variable';
  return COMPARABILITY_VARIABLES[name] || String(name).replace(/_/g, ' ');
}

/**
 * The comparability band shown above every comparison, and next to a delta on /runs.
 *
 * @param {{ verdict?: string, differing?: string[], reason?: string }|string|null} comparability
 * @returns {{ verdict: string, label: string, color: string, icon: string, text: string,
 *   differing: string[], attributable: boolean, muted: boolean }}
 */
export function comparabilityBand(comparability) {
  const raw = typeof comparability === 'string' ? { verdict: comparability } : comparability || {};
  const differing = (Array.isArray(raw.differing) ? raw.differing : []).filter(Boolean);
  const verdict =
    typeof raw.verdict === 'string' && raw.verdict ? raw.verdict.toLowerCase() : 'not_attributable';

  if (verdict === 'comparable') {
    return {
      verdict,
      label: 'Comparable',
      color: 'success',
      icon: 'mdi-equal',
      text: 'Comparable: same folder, configuration and engine versions.',
      differing,
      attributable: true,
      muted: false,
    };
  }

  if (verdict === 'attributable') {
    const name = variableLabel(differing[0]);
    return {
      verdict,
      label: 'Attributable',
      color: 'warning',
      icon: 'mdi-alert-outline',
      text: `Only ${name} differs. The delta is attributable to it.`,
      differing,
      attributable: true,
      muted: false,
    };
  }

  // Anything that is not explicitly comparable/attributable is treated as not attributable: a
  // delta must never look trustworthy just because the verdict was unreadable.
  const n = differing.length;
  const text = n
    ? `${n} ${n === 1 ? 'variable differs' : 'variables differ'}. This delta is not attributable to any single change.`
    : 'Several variables differ. This delta is not attributable to any single change.';
  return {
    verdict: 'not_attributable',
    label: 'Not attributable',
    color: 'error',
    icon: 'mdi-alert-circle-outline',
    text,
    differing,
    attributable: false,
    muted: true,
  };
}

/** Short suffix printed next to a delta on the runs list ('config changed', 'not attributable'). */
export function deltaCaption(delta) {
  if (!delta) return '';
  const band = comparabilityBand(delta);
  if (band.verdict === 'comparable') return 'same conditions';
  if (band.verdict === 'attributable') return `${variableLabel(band.differing[0])} changed`;
  return band.differing.length
    ? `not attributable · ${band.differing.map(variableLabel).join(', ')}`
    : 'not attributable';
}

/* -------------------------------------------------------------------------- */
/* Status, progress and times                                                  */
/* -------------------------------------------------------------------------- */

const STATUS_PRESENTATION = Object.freeze({
  QUEUED: { label: 'Queued', color: 'grey', icon: 'mdi-clock-outline', running: true },
  RUNNING: { label: 'Running', color: 'primary', icon: 'mdi-progress-clock', running: true },
  COMPLETED: { label: 'Completed', color: 'success', icon: 'mdi-check-circle-outline' },
  PARTIAL: { label: 'Partial', color: 'warning', icon: 'mdi-alert-outline' },
  FAILED: { label: 'Failed', color: 'error', icon: 'mdi-alert-circle-outline' },
  CANCELLED: { label: 'Cancelled', color: 'grey darken-2', icon: 'mdi-close-circle-outline' },
});

export function folderRunStatusPresentation(status) {
  const key = typeof status === 'string' ? status.trim().toUpperCase() : '';
  return (
    STATUS_PRESENTATION[key] || {
      label: status ? String(status) : 'Unknown',
      color: 'grey',
      icon: 'mdi-help-circle-outline',
    }
  );
}

/** A run still moving: the list polls while at least one row answers true. */
export function isFolderRunActive(run) {
  const key = typeof run?.status === 'string' ? run.status.trim().toUpperCase() : '';
  return key === 'QUEUED' || key === 'RUNNING';
}

/** '7/12 · 58 %' — the spec's exact shape, with a thin space before the percent sign. */
export function progressText(run) {
  if (!run) return EMPTY;
  const counts = run.counts || {};
  const total = toNumber(counts.total) ?? toNumber(run.videos_count) ?? 0;
  const pending = toNumber(run.pending);
  const done =
    pending !== null && total
      ? Math.max(0, total - pending)
      : (toNumber(counts.succeeded) || 0) +
        (toNumber(counts.failed) || 0) +
        (toNumber(counts.cancelled) || 0);
  const pct = toNumber(run.progress_pct);
  const percent = pct !== null ? pct : total ? Math.round((done / total) * 100) : 0;
  return `${done}/${total} · ${percent} %`;
}

export function progressValue(run) {
  const pct = toNumber(run?.progress_pct);
  if (pct !== null) return Math.min(100, Math.max(0, pct));
  const total = toNumber(run?.counts?.total) ?? toNumber(run?.videos_count) ?? 0;
  const pending = toNumber(run?.pending) ?? 0;
  if (!total) return 0;
  return Math.min(100, Math.max(0, Math.round(((total - pending) / total) * 100)));
}

/** '45 s', '3 min 20 s', '1 h 04 min'. Null / negative / unparseable reads as an em dash. */
export function formatDurationMs(value) {
  const ms = toNumber(value);
  if (ms === null || ms < 0) return EMPTY;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return `${hours} h ${String(restMinutes).padStart(2, '0')} min`;
}

/** Elapsed while running, total duration once finished, em dash when neither is known. */
export function formatRunTime(run) {
  if (!run) return EMPTY;
  const duration = toNumber(run.duration_ms);
  if (duration !== null) return formatDurationMs(duration);
  const elapsed = toNumber(run.elapsed_ms);
  if (elapsed !== null) return formatDurationMs(elapsed);
  return EMPTY;
}

export function formatDateTime(value) {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

export function shortHash(value, length = 8) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).slice(0, length);
}

/* -------------------------------------------------------------------------- */
/* Per-video outcomes                                                          */
/* -------------------------------------------------------------------------- */

const OUTCOME_PRESENTATION = Object.freeze({
  detected: {
    label: 'Detected',
    color: 'success',
    icon: 'mdi-check-decagram-outline',
    tooltip: 'Labelled as a lie and flagged by the system.',
  },
  missed: {
    label: 'Missed',
    color: 'error',
    icon: 'mdi-alert-circle-outline',
    tooltip: 'Labelled as a lie but not flagged.',
  },
  false_positive: {
    label: 'False alarm',
    color: 'warning',
    icon: 'mdi-alert-outline',
    tooltip: 'Labelled as truthful but flagged by the system.',
  },
  cleared: {
    label: 'Cleared',
    color: 'primary',
    icon: 'mdi-shield-check-outline',
    tooltip: 'Labelled as truthful and not flagged.',
  },
});

/** One of the four Coherence Evaluation outcomes; anything else is "not evaluated". */
export function outcomePresentation(outcome) {
  const key = typeof outcome === 'string' ? outcome.trim().toLowerCase() : '';
  return (
    OUTCOME_PRESENTATION[key] || {
      label: EMPTY,
      color: 'grey',
      icon: 'mdi-minus',
      tooltip: 'Not labelled, or the analysis did not finish.',
      empty: true,
    }
  );
}

export function labelPresentation(label) {
  const key = typeof label === 'string' ? label.trim().toLowerCase() : '';
  if (key === 'incoherence') return { label: 'Lie', color: 'error' };
  if (key === 'truthful') return { label: 'Truthful', color: 'success' };
  return { label: 'Unlabelled', color: 'grey', empty: true };
}

/* -------------------------------------------------------------------------- */
/* Engine versions                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Engine version chips, patch included, alphabetical. Engines listed in `outdatedEngines` are
 * highlighted so the row shows WHICH engine moved, not just that something did.
 *
 * @param {Record<string,string>|null} engineVersions
 * @param {{ outdatedEngines?: Array<{engine: string, run: string, current: string}> }} options
 * @returns {Array<{ key: string, text: string, color: string|null, outdated: boolean, tooltip: string }>}
 */
export function engineVersionChips(engineVersions, { outdatedEngines = [] } = {}) {
  if (!engineVersions || typeof engineVersions !== 'object') return [];
  const stale = new Map(
    (Array.isArray(outdatedEngines) ? outdatedEngines : [])
      .filter((entry) => entry && entry.engine)
      .map((entry) => [String(entry.engine), entry])
  );
  return Object.keys(engineVersions)
    .sort()
    .map((engine) => {
      const version = engineVersions[engine];
      const entry = stale.get(engine) || null;
      return {
        key: `engine-${engine}`,
        text: `${engine} ${version}`,
        color: entry ? 'warning' : null,
        outdated: Boolean(entry),
        tooltip: entry
          ? `${engine}: this run used ${entry.run ?? EMPTY}, deployed is ${entry.current ?? EMPTY}`
          : `${engine} engine version ${version}`,
      };
    });
}

/** Descending semver comparator, tolerant of pre-release suffixes and missing parts. */
export function compareSemverDesc(a, b) {
  const parse = (value) =>
    String(value ?? '')
      .split('-')[0]
      .split('.')
      .map((part) => {
        const n = Number(part);
        return Number.isFinite(n) ? n : 0;
      });
  const left = parse(a);
  const right = parse(b);
  const length = Math.max(left.length, right.length, 3);
  for (let i = 0; i < length; i += 1) {
    const diff = (right[i] || 0) - (left[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                        */
/* -------------------------------------------------------------------------- */

/** 'Folder A · 12 videos', the identity line of every run row. */
export function folderCaption(run) {
  if (!run) return EMPTY;
  const name = run.folder?.name || run.folder_name || 'Unknown folder';
  const count = toNumber(run.videos_count) ?? toNumber(run.counts?.total);
  if (count === null) return name;
  return `${name} · ${count} ${count === 1 ? 'video' : 'videos'}`;
}

/** 'config v3 · 1f4c9a2b' or 'config: current' when the version was not recorded. */
export function configCaption(config) {
  if (!config) return 'config: current';
  const version = toNumber(config.version);
  const hash = shortHash(config.engines_hash);
  const parts = [version !== null ? `config v${version}` : 'config: current'];
  if (hash) parts.push(hash);
  return parts.join(' · ');
}

const LAUNCH_ERROR_MESSAGES = Object.freeze({
  folder_empty: 'That folder has no videos, so there is nothing to run.',
  folder_not_found: 'That folder no longer exists.',
  config_required: 'Pick an analysis configuration for this run.',
});

/** Turns an axios error from POST /folder-runs into one sentence the user can act on. */
export function describeLaunchError(error) {
  const data = error?.response?.data?.data ?? error?.response?.data ?? null;
  const code = data?.error || data?.code || data?.reason;
  if (code && LAUNCH_ERROR_MESSAGES[code]) return LAUNCH_ERROR_MESSAGES[code];
  const rejected = Array.isArray(data?.rejected) ? data.rejected : [];
  if (rejected.length) {
    const reasons = Array.from(new Set(rejected.map((item) => item?.reason).filter(Boolean)));
    const detail = reasons.length ? ` (${reasons.join(', ')})` : '';
    return `No video could be queued${detail}.`;
  }
  return data?.message || error?.message || 'Could not launch the run.';
}
